import React, { useState } from 'react';
import {
  Box, Divider, IconButton, makeStyles, useMediaQuery,
  useTheme, Tooltip, Popover
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import moment from 'moment';
import 'moment/locale/hu';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowDown, KeyboardArrowRight, MoreHoriz } from '@material-ui/icons';
import clsx from 'clsx';

import { isAllRaterClosed } from '../../../utils/calculations.js';
import { STATUS, DEVELOPMENT_STATUS, OWNER_TYPES } from '../../../utils/const';
import { RecomendedIcon,
  CompletedIcon,
  FailedIcon,
  InDangerIcon,
  NeutralIcon,
  AwesomeIcon } from '../../../assets/icons/index';
import ObjectiveStatusChip from '../../StatusButtons/ObjectiveStatusChip';
import RatingStatusChip from '../../RatingStatusChip';
import DataDisplay from '../../DataDisplay';
import Delete from '@material-ui/icons/Delete';

const useStyles = makeStyles(theme => ({
  itemHeader: {
    display: 'grid',
    width: '100%',
    gridGap: 8,
    alignItems: 'center',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gridTemplateAreas: `
      'name     name     actions  actions'
      'divider1 divider1 divider1   divider1'
      'category deadline weight     mean'
      'divider2 divider2 divider2   divider2'
      'status   status   rating     rating'
      `,
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr 75px 65px 1fr 1fr 65px 96px',
      gridTemplateAreas: `
        'name category deadline weight status rating mean actions'
      `
    }
  },
  actions: {
    marginLeft: 'auto'
  },
  groupColumns: {
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gridTemplateAreas: `
      'name     name     actions    actions'
      'divider1 divider1 divider1   divider1'
      'category deadline weight     mean'
      'divider2 divider2 divider2   divider2'
      'status   status   rating     rating'
      'divider3 divider3 divider3   divider3'
      'group    group    appraiser  appraiser'
      `,
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr 1fr 1fr 75px 65px 1fr 1fr 65px 96px',
      gridTemplateAreas: `
        'name category group appraiser deadline weight status rating mean actions'
      `
    }
  },
  tooltip: {
    fontSize: '12px'
  }
}), { name: 'ObjectiveSummary' });

const ObjectiveSummary = ({
  isPanelOpened,
  objective,
  setUpdateDialogOpen,
  setDeleteDialogOpen,
  isEditable,
  isDeletable,
  responsiveItems = true
}) => {
  moment.locale('hu');
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));

  const { IN_DANGER, NEUTRAL, AWESOME } = DEVELOPMENT_STATUS;
  const { PROPOSED, FAILED, ACTIVE, COMPLETED } = STATUS;

  const [ actionsOpen, setActionsOpen ] = useState(null);

  const developmentStatusTitle = developmentStatus => {
    switch (developmentStatus) {
      case IN_DANGER:
        return t('objective.development.status.in-danger-title');
      case NEUTRAL:
        return t('objective.development.status.neutral-title');
      case AWESOME:
        return t('objective.development.status.awesome-title');
      default:
        return t('objective.development.status.neutral-title');
    };
  };

  const renderIconWithTooltip = (Icon, developmentStatus) => (
    <Tooltip   classes={ { tooltip: classes.tooltip } }
      placement="top"
      title={ developmentStatusTitle(developmentStatus) }
      arrow
    >
      <div><Icon/></div>
    </Tooltip>
  );

  const renderStatusIcon = (status, developmentStatus) => {
    switch (status) {
      case PROPOSED:
        return <RecomendedIcon />;
      case FAILED:
        return <FailedIcon />;
      case ACTIVE:
        switch (developmentStatus) {
          case IN_DANGER:
            return renderIconWithTooltip(InDangerIcon, developmentStatus);
          case NEUTRAL:
            return renderIconWithTooltip(NeutralIcon, developmentStatus);
          case AWESOME:
            return renderIconWithTooltip(AwesomeIcon, developmentStatus);
          default:
            return renderIconWithTooltip(NeutralIcon, developmentStatus);
        }
      case COMPLETED:
        return <CompletedIcon />;
      default:
        return <RecomendedIcon />;
    }
  };

  let mean = '-';
  if (
    isAllRaterClosed(objective?.appraisers_and_criterias?.appraisers) &&
    (objective.ratingsData.weightedMean ||
      objective.ratingsData.weightedMean === 0)
  ) {
    mean = `${objective.ratingsData.weightedMean.toFixed(2)}%`;
  }

  const isGroupObjective = objective.owner[0]?.type === OWNER_TYPES.GROUP;

  const editButton = isEditable && (
    <IconButton
      onClick={ e => {
        e.stopPropagation();
        setActionsOpen(false);
        setUpdateDialogOpen(true);
      } }
    >
      <EditIcon />
    </IconButton>
  );

  const deleteButton = isDeletable && (
    <IconButton onClick={ e => {
      e.stopPropagation();
      setActionsOpen(false);
      setDeleteDialogOpen(true);
    } } >
      <Delete />
    </IconButton>
  );

  return (
    <div className={ clsx(classes.itemHeader, {
      [classes.groupColumns]: isGroupObjective
    }) }>
      <DataDisplay
        area={ 'name' }
        title={ t('objectiveList.header.name') }
        responsive={ responsiveItems }
      >
        {objective.name}
      </DataDisplay>
      <DataDisplay
        area={ 'category' }
        title={ t('objectiveList.header.category') }
        responsive={ responsiveItems }
      >
        {objective.category ?? '-'}
      </DataDisplay>
      {isGroupObjective &&
        <>
          <DataDisplay
            area={ 'group' }
            title={ t('objectiveList.header.group') }
            responsive={ responsiveItems }
          >
            {objective.owner[0]?.name ?? '-'}
          </DataDisplay>
          <DataDisplay
            area={ 'appraiser' }
            title={ t('objectiveList.header.appraiser') }
            responsive={ responsiveItems }
          >
            {objective.appraisers_and_criterias?.appraisers[0]?.name}
          </DataDisplay>
        </>
      }
      <DataDisplay
        area={ 'deadline' }
        title={ t('objectiveList.header.deadline') }
        responsive={ responsiveItems }
      >
        {moment(objective.deadline).format('L')}
      </DataDisplay>
      <DataDisplay
        area={ 'weight' }
        title={ t('objectiveList.header.weight') }
        responsive={ responsiveItems }
      >
        {objective.weight}
      </DataDisplay>
      <DataDisplay
        area={ 'status' }
        title={ t('objectiveList.header.status') }
        responsive={ responsiveItems }
      >
        <Box display="flex" alignItems="center">
          {renderStatusIcon(objective.status, objective.development_status)}
          <ObjectiveStatusChip status={ objective.status } />
        </Box>
      </DataDisplay>
      <DataDisplay
        area={ 'rating' }
        title={ t('objectiveList.header.rating') }
        responsive={ responsiveItems }
      >
        <RatingStatusChip ratingStatus={ objective.ratingStatus } />
      </DataDisplay>
      <DataDisplay
        area={ 'mean' }
        title={ t('objectiveList.header.result') }
        responsive={ responsiveItems }
      >
        {mean}
      </DataDisplay>
      <div style={ { gridArea: 'actions' } } className={ classes.actions }>
        {
          editButton && deleteButton ? (
            <>
              <IconButton
                onClick={ e => {
                  e.stopPropagation();
                  setActionsOpen(e.currentTarget);
                } }
              >
                <MoreHoriz />
              </IconButton>
              <Popover
                open={ !!actionsOpen }
                anchorEl={ actionsOpen }
                onClose={ () => setActionsOpen(false) }

              >
                {editButton}
                {deleteButton}
              </Popover>
            </>
          ) : <>
            {editButton}
            {deleteButton}
          </>
        }
        {isPanelOpened !== undefined &&
          <IconButton size={ md ? 'medium' : 'small' }>
            {isPanelOpened ? (
              <KeyboardArrowDown />
            ) : (
              <KeyboardArrowRight />
            )}
          </IconButton>
        }
      </div>
      {!md && (
        <>
          <div style={ { gridArea: 'divider1' } }>
            <Divider />
          </div>
          <div style={ { gridArea: 'divider2' } }>
            <Divider />
          </div>
          {isGroupObjective &&
            <div style={ { gridArea: 'divider3' } }>
              <Divider />
            </div>
          }
        </>
      )}
    </div>
  );
};

export default ObjectiveSummary;
